import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, ShieldCheck, HeartHandshake, ArrowRight } from "lucide-react";

const NASSAU = "/assets/about-friends.webp";
const G_WAGON = "/vehicles/silverado.webp";

export default function About() {
  useSEO({ title: "About Easy Drive — Family-Run Car Rental in Nassau Bahamas", description: "Easy Drive Car Rental is a family-run Bahamian business on West Bay Street, Nassau. Showroom-condition vehicles, transparent pricing and real humans on WhatsApp.", path: "/about" });
  return (
    <div data-testid="about-page" className="pt-32 pb-12">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Our Story</p>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tight max-w-3xl leading-[1.05]">
          Nassau born. <span className="italic gold-text">Island driven.</span>
        </h1>
        <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed text-base md:text-lg">
          Easy Drive Car Rental was founded on West Bay Street with a simple idea:
          renting a car in the Bahamas should feel like borrowing one from a friend —
          if that friend kept a fleet of showroom-condition vehicles and answered
          their phone at 2 AM.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden border border-border">
          <img src={NASSAU} alt="Friends enjoying an Easy Drive rental along the Nassau coast" className="w-full max-h-[80vh] object-cover" loading="lazy" />
        </div>
        <div>
          <h2 className="font-display text-4xl tracking-tight">
            Local team, <span className="italic gold-text">premium standards</span>
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            We're a family-run Bahamian business. Every vehicle is detailed, sanitized
            and safety-checked between rentals, and every booking comes with real
            humans on WhatsApp — not a call center halfway around the world.
          </p>
          <div className="mt-8 space-y-5">
            {[
              { icon: MapPin, t: "We come to you", d: "Free delivery to the cruise port, Cable Beach, Paradise Island and any hotel or villa on New Providence — plus airport drop-off when you fly out." },
              { icon: ShieldCheck, t: "Insured & transparent", d: "Basic insurance included on every rental. Digital contracts you can read and sign from your phone before you even land." },
              { icon: HeartHandshake, t: "Pay how you like", d: "Secure credit card checkout online, or send payment by PayPal or Zelle. Confirmation emails for every booking." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} data-testid={`about-value-${i}`} className="flex gap-4">
                <div className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
                  <Icon size={16} className="gold-text" />
                </div>
                <div>
                  <p className="font-ui font-semibold text-sm">{t}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <h2 className="font-display text-4xl tracking-tight">
            From compact runabouts to <span className="italic gold-text">full-size trucks</span>
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Whether you're here for a cruise-ship day trip, a week at Baha Mar, or a
            month-long island escape, we have a vehicle that fits — and a rate that
            makes sense. Unlimited mileage on New Providence comes standard.
          </p>
          <Link to="/fleet" className="inline-block mt-8">
            <Button data-testid="about-browse-fleet-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8">
              Browse the Fleet <ArrowRight size={15} className="ml-2" />
            </Button>
          </Link>
        </div>
        <div className="order-1 lg:order-2 rounded-2xl overflow-hidden border border-border">
          <img src={G_WAGON} alt="Easy Drive Chevrolet Silverado, part of the real Nassau fleet" className="w-full max-h-[80vh] object-cover" loading="lazy" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 bg-card border border-border rounded-2xl p-10 md:p-14">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Visit us</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Office</p>
            <p className="mt-2 text-foreground/90">West Bay Street<br />Nassau, New Providence, Bahamas</p>
          </div>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Talk to us</p>
            <p className="mt-2 text-foreground/90">+1 (242) 813-1775<br />bookings@easydrive242.com</p>
          </div>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Hours</p>
            <p className="mt-2 text-foreground/90">Airport drop-off 24/7<br />Office: Mon–Sat, 8 AM – 6 PM</p>
          </div>
        </div>
      </section>
    </div>
  );
}
